import { Separator as SeparatorPrimitive } from "@base-ui/react/separator";

import { cn } from "../../lib/utils";

/**
 * Migrated from `@radix-ui/react-separator` to Base UI.
 *
 * The public API is unchanged and none of the 44 consumers changes. There is
 * only one difference, and it is invisible: **Base UI has no `decorative`
 * prop.**
 *
 * Radix's `decorative` decides whether the rule exists for assistive
 * technology. This component defaults it to `true`, so today all 44 consumers
 * render `role="none"` — correct for a line that is pure visual garnish, and
 * exactly what the ARIA practices ask for. Base UI's separator always renders
 * `role="separator"` with an `aria-orientation`, so a straight swap would start
 * announcing every one of them, with identical pixels, identical classes and no
 * type error to show for it.
 *
 * So `decorative` is absorbed here rather than dropped: when it is set — the
 * default — the element is handed back to the accessibility tree as `none` and
 * the `aria-orientation` Base UI added is removed again. Both overrides are
 * passed after the primitive's own props so they win the merge.
 *
 * The exact ARIA shape was measured rather than assumed. Radix **omits**
 * `aria-orientation` on a *horizontal* semantic separator, because horizontal is
 * the role's default; only the vertical case carries it. Base UI always emits
 * it, so the horizontal semantic case drops it here too, keeping the rendered
 * output identical to what shipped.
 *
 * `data-orientation` is spelled the same by both, so the
 * `data-[orientation=…]` variants that give this component its only dimensions
 * are untouched.
 */
function Separator({
  className,
  orientation = "horizontal",
  decorative = true,
  ...props
}: Omit<SeparatorPrimitive.Props, "className"> & {
  className?: string;
  /**
   * Radix's flag for "this line is visual garnish, keep it out of the
   * accessibility tree". Base UI has no equivalent, so it is implemented here.
   * Defaults to `true`, as it has since the component was vendored.
   */
  decorative?: boolean;
}) {
  return (
    <SeparatorPrimitive
      data-slot="separator"
      orientation={orientation}
      className={cn(
        "bg-border shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px",
        className,
      )}
      {...props}
      {...(decorative
        ? { role: "none", "aria-orientation": undefined }
        : // Horizontal is the ARIA default for `role="separator"`, and Radix
          // left it implicit. Base UI states it explicitly; dropping it back
          // keeps the DOM identical to what shipped.
          orientation === "horizontal"
          ? { "aria-orientation": undefined }
          : {})}
    />
  );
}

export { Separator };
